"use client";

import { quizLinkLabel } from "@/lib/i18n/quiz-link-language";

import type { Route } from "next";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";

import {
  createQuizAttemptId,
  trackQuizEvent,
  type QuizLoadFailureReason,
  type QuizStudyContext,
} from "@/lib/quiz-analytics";
import { quizIdToUrl } from "@/lib/quizzes/quiz-slugs";
import { getQuizOptionOrder } from "./option-order";
import { loadQuizData, type QuizId } from "./registry";
import { renderInlineMath } from "./render-math";
import type { McqQuestion, QuizData } from "./types";

import type { Locale } from "@/lib/i18n/locales";
import { quickCheckCopy } from "@/lib/i18n/quick-check-copy";

export type QuickCheckProps = {
  locale?: Locale;
  fullQuizHref?: Route;
  quizId: QuizId;
  questionId: number;
  placementId: string;
  sectionId: string;
  feedback?: string;
  initialQuizData?: QuizData;
  /** Original bank positions for an authored question subset. */
  questionNumbersById?: Record<number, number>;
  studyContext?: QuizStudyContext | null;
};

export default function QuickCheck({
  quizId,
  questionId,
  placementId,
  sectionId,
  feedback,
  initialQuizData,
  questionNumbersById,
  locale = "en",
  fullQuizHref,
  studyContext = null,
}: QuickCheckProps) {
  const copy = quickCheckCopy(locale);
  const [quizData, setQuizData] = useState<QuizData | null>(
    initialQuizData ?? null
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [canRetry, setCanRetry] = useState(false);
  const [isLoading, setIsLoading] = useState(!initialQuizData);
  const trackedLoadFailuresRef = useRef(new Set<string>());
  const studySubject = studyContext?.studySubject;
  const studyTopic = studyContext?.studyTopic;

  const reportLoadFailure = useCallback(
    (failureReason: QuizLoadFailureReason) => {
      const failureKey = `${quizId}:${questionId}:${failureReason}`;
      if (trackedLoadFailuresRef.current.has(failureKey)) return;
      trackedLoadFailuresRef.current.add(failureKey);
      trackQuizEvent("quiz_load_failed", {
        quizId,
        surface: "embedded",
        mode: "quick-check",
        question_id: questionId,
        placement_id: placementId,
        section_id: sectionId,
        failure_reason: failureReason,
        ...(studySubject && { studySubject }),
        ...(studyTopic && { studyTopic }),
      });
    },
    [placementId, questionId, quizId, sectionId, studySubject, studyTopic]
  );

  useEffect(() => {
    setCanRetry(false);
    if (initialQuizData) {
      const hasRequestedQuestion = initialQuizData.mcqQuestions?.some(
        (question) => question.id === questionId
      );
      if (!hasRequestedQuestion) {
        setQuizData(initialQuizData);
        setLoadError(copy.unavailable);
        setIsLoading(false);
        reportLoadFailure("question_not_found");
        return;
      }
      setQuizData(initialQuizData);
      setLoadError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    loadQuizData(quizId)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setLoadError(copy.unavailable);
          reportLoadFailure("not_found");
          return;
        }
        if (
          !data.mcqQuestions?.some((question) => question.id === questionId)
        ) {
          setQuizData(data);
          setLoadError(copy.unavailable);
          reportLoadFailure("question_not_found");
          return;
        }
        setQuizData(data);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(copy.unavailable);
          setCanRetry(true);
          reportLoadFailure("load_error");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    initialQuizData,
    questionId,
    quizId,
    reportLoadFailure,
    loadAttempt,
    copy.unavailable,
  ]);

  if (isLoading) {
    return (
      <div className="not-prose my-10 animate-pulse border-y border-slate-200 py-6 sm:py-7">
        <div className="h-5 w-4/5 rounded bg-slate-200" />
        <div className="mt-4 divide-y divide-slate-200 border-t border-slate-200">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="flex min-h-11 items-center gap-3 py-3">
              <span className="h-6 w-6 shrink-0 rounded-full bg-slate-200" />
              <span className="h-4 flex-1 rounded bg-slate-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const questions = quizData?.mcqQuestions ?? [];
  const questionIndex = questions.findIndex(
    (question) => question.id === questionId
  );
  const question = questions[questionIndex];

  if (!question || loadError) {
    return (
      <div className="not-prose my-8 border-l-2 border-amber-400 pl-4 text-sm leading-6 text-amber-900">
        {loadError ?? copy.unavailable}
        {canRetry && (
          <button
            type="button"
            className="mt-3 block min-h-11 px-3 font-semibold underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={() => setLoadAttempt((attempt) => attempt + 1)}
          >
            {copy.retry}
          </button>
        )}
      </div>
    );
  }

  return (
    <QuickCheckQuestion
      quizId={quizId}
      question={question}
      questionNumber={questionNumbersById?.[questionId] ?? questionIndex + 1}
      placementId={placementId}
      sectionId={sectionId}
      feedback={feedback}
      locale={locale}
      fullQuizHref={fullQuizHref}
      studyContext={studyContext}
    />
  );
}

type QuickCheckQuestionProps = {
  locale: Locale;
  fullQuizHref?: Route;
  quizId: QuizId;
  question: McqQuestion;
  questionNumber: number;
  placementId: string;
  sectionId: string;
  feedback?: string;
  studyContext: QuizStudyContext | null;
};

function QuickCheckQuestion({
  quizId,
  question,
  questionNumber,
  placementId,
  sectionId,
  feedback,
  locale,
  fullQuizHref,
  studyContext,
}: QuickCheckQuestionProps) {
  const copy = quickCheckCopy(locale);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isInteractive, setIsInteractive] = useState(false);
  const rootRef = useRef<HTMLElement>(null);
  const attemptIdRef = useRef(createQuizAttemptId());
  const visibleRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const studySubject = studyContext?.studySubject;
  const studyTopic = studyContext?.studyTopic;

  const orderedOptions = useMemo(
    () => getQuizOptionOrder(quizId, question),
    [question, quizId]
  );

  const eventContext = useCallback(
    () => ({
      quizId,
      attemptId: attemptIdRef.current,
      surface: "embedded" as const,
      mode: "quick-check" as const,
      total: 1,
      question_id: question.id,
      question_number: questionNumber,
      placement_id: placementId,
      section_id: sectionId,
      ...(studySubject && { studySubject }),
      ...(studyTopic && { studyTopic }),
    }),
    [
      placementId,
      question.id,
      questionNumber,
      quizId,
      sectionId,
      studySubject,
      studyTopic,
    ]
  );

  useEffect(() => {
    setSelectedAnswer(null);
    visibleRef.current = false;
    startedAtRef.current = null;
    attemptIdRef.current = createQuizAttemptId();
    trackQuizEvent("quiz_viewed", eventContext());
    setIsInteractive(true);
  }, [eventContext]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return;

    let visibilityTimer: number | null = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (visibleRef.current) return;
        if (!entry?.isIntersecting || entry.intersectionRatio < 0.5) {
          if (visibilityTimer !== null) {
            window.clearTimeout(visibilityTimer);
            visibilityTimer = null;
          }
          return;
        }
        if (visibilityTimer !== null) return;
        visibilityTimer = window.setTimeout(() => {
          visibleRef.current = true;
          observer.disconnect();
          trackQuizEvent("quiz_visible", {
            ...eventContext(),
            elapsed_seconds: 1,
          });
        }, 1000);
      },
      { threshold: 0.5 }
    );

    observer.observe(root);
    return () => {
      if (visibilityTimer !== null) window.clearTimeout(visibilityTimer);
      observer.disconnect();
    };
  }, [eventContext]);

  const chooseAnswer = (optionIndex: number) => {
    if (selectedAnswer !== null) return;

    startedAtRef.current = Date.now();
    const answerCorrect = optionIndex === question.correct;
    const context = eventContext();

    trackQuizEvent("quiz_started", context);
    trackQuizEvent("quiz_answered", {
      ...context,
      questions_answered: 1,
      answer_correct: answerCorrect,
      elapsed_seconds: 0,
    });
    trackQuizEvent("quiz_completed", {
      ...context,
      score: answerCorrect ? 1 : 0,
      questions_answered: 1,
      answer_correct: answerCorrect,
      elapsed_seconds: 0,
      wrongIds: answerCorrect ? [] : [question.id],
    });
    setSelectedAnswer(optionIndex);
  };

  const selectedIsCorrect = selectedAnswer === question.correct;
  const correctAnswer = question.options[question.correct];
  const explanation =
    feedback?.trim() || question.explanation.replace(/^Key idea:\s*/i, "");

  return (
    <article
      ref={rootRef}
      className="not-prose my-10 border-y border-slate-200 py-6 sm:py-7"
      aria-labelledby={`${placementId}-question`}
      aria-busy={!isInteractive}
    >
      <h3
        id={`${placementId}-question`}
        className="text-base font-semibold leading-7 text-slate-950 sm:text-lg"
      >
        {renderInlineMath(question.question, `${placementId}-q`)}
      </h3>

      <div className="mt-4 divide-y divide-slate-200 border-t border-slate-200">
        {orderedOptions.map(({ option, originalIndex }, displayIndex) => {
          const isSelected = selectedAnswer === originalIndex;
          const isCorrect = originalIndex === question.correct;
          const resultClasses =
            selectedAnswer === null
              ? "text-slate-800 hover:bg-slate-50"
              : isCorrect
              ? "bg-emerald-50/70 text-emerald-950"
              : isSelected
              ? "bg-rose-50/70 text-rose-950"
              : "text-slate-500";

          return (
            <button
              key={`${question.id}-${originalIndex}`}
              type="button"
              onClick={() => chooseAnswer(originalIndex)}
              disabled={!isInteractive || selectedAnswer !== null}
              aria-pressed={isSelected}
              className={`flex min-h-11 w-full items-start gap-3 px-1 py-3 text-left text-sm leading-6 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                !isInteractive ? "cursor-wait opacity-60" : ""
              } ${resultClasses}`}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-current text-xs font-semibold">
                {String.fromCharCode(65 + displayIndex)}
              </span>
              <span className="flex-1">
                {renderInlineMath(
                  option,
                  `${placementId}-option-${originalIndex}`
                )}
              </span>
              {selectedAnswer !== null && isCorrect ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              ) : null}
              {selectedAnswer !== null && isSelected && !isCorrect ? (
                <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
              ) : null}
            </button>
          );
        })}
      </div>

      {selectedAnswer !== null ? (
        <div
          className={`mt-5 border-l-2 pl-4 text-sm leading-6 text-slate-700 ${
            selectedIsCorrect ? "border-emerald-500" : "border-rose-400"
          }`}
          role="status"
        >
          <p
            className={`flex items-start gap-2 font-semibold ${
              selectedIsCorrect ? "text-emerald-800" : "text-rose-800"
            }`}
          >
            {selectedIsCorrect ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span>
              {selectedIsCorrect ? (
                copy.correct
              ) : (
                <>
                  {copy.incorrect}{" "}
                  {renderInlineMath(
                    correctAnswer,
                    `${placementId}-correct-answer`
                  )}
                  .
                </>
              )}
            </span>
          </p>
          <p className="mt-1">
            {renderInlineMath(explanation, `${placementId}-explanation`)}
          </p>
          <Link<string>
            href={(fullQuizHref ?? quizIdToUrl(quizId)) as Route}
            onClick={() =>
              trackQuizEvent("quick_check_full_quiz_click", {
                ...eventContext(),
                score: selectedIsCorrect ? 1 : 0,
                questions_answered: 1,
                answer_correct: selectedIsCorrect,
                elapsed_seconds: startedAtRef.current
                  ? Math.round((Date.now() - startedAtRef.current) / 1000)
                  : undefined,
              })
            }
            className="mt-2 inline-flex font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-900"
          >
            {quizLinkLabel(
              copy.fullQuiz,
              locale,
              fullQuizHref ?? quizIdToUrl(quizId)
            )}
          </Link>
        </div>
      ) : null}
    </article>
  );
}
